import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Button from '@mui/material/Button';
import CircularProgress  from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormGroup from '@mui/material/FormGroup';
import InputLabel from '@mui/material/InputLabel';
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import FormHelperText from '@mui/material/FormHelperText';

import React, { ReactElement, useEffect, useState } from 'react';
import {  uuid } from '../../../../utils/utils';
import { toast } from 'react-toastify';
import { useWorkspaceContext } from '../../../../context/workspaceContext';
import { CanvasEnvironment } from '../../../../utils/canvas/canvas-environment';
import * as CanvasActions from '../../../../utils/canvas/canvas-actions';
import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';

import { CanvasActionType } from '../../../../@types/canvas-action-type';
import IRPService from '../../../../services/irp.service';
import { IntegratedRiskPicture, } from '../../../../@types/integrated-risk-picture';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

function forceUTCDate(referenceDate: Value): string | null { 
    if (referenceDate === null) {
        return null;
    }
    if (Array.isArray(referenceDate)) {
        let start = referenceDate[0] as Date;
        let end = referenceDate[1] as Date;
        let tzOffset = start.getTimezoneOffset();
        let startDate = new Date();
        startDate.setTime(start.getTime() - tzOffset * 60 * 1000);
        let endDate = new Date();
        endDate.setTime(end.getTime() - tzOffset * 60 * 1000);
        return JSON.stringify([startDate, endDate]);
    }
    let tzOffset = referenceDate.getTimezoneOffset();
    let date = new Date();
    date.setTime(referenceDate.getTime() - tzOffset * 60 * 1000);
    return date.toJSON();
}


function PaperComponent(props: PaperProps): ReactElement {
    const nodeRef = React.useRef(null);
    return (
        <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

export default function NodeOccurrenceListComponentModal(props: { open: boolean, env: CanvasEnvironment | null, onClose: () => void }): ReactElement {
    const { open, env, onClose } = props;
    const [currentLinkedIrp, setCurrentLinkedIrp] = useState<IntegratedRiskPicture | null>(null);
    const [eventSequenceLinkedIrps, setEventSequenceLinkedIrps] = React.useState<Array<IntegratedRiskPicture>>([]);
    const [startValue, onChangeStartValue] = useState<Value>(new Date('2000-01-01T00:00:00.000'));
    const [endValue, onChangeEndValue] = useState<Value>(new Date('2050-01-01T00:00:00.000'));
    const [isRunning, setIsRunning] = useState<boolean>(false);
    const { setCurrentCanvasAction } = useWorkspaceContext();
    const uuidRef = React.useRef(uuid());

    useEffect(() => {
        if (open) {
            setIsRunning(true);
            setCurrentLinkedIrp(null);
            let linkedIrpsPromise = IRPService.getLinkedIrpsForEventSequence(env!.eventSequence!.id);
            setIsRunning(true);
            Promise.all([linkedIrpsPromise]).then((responses) => {
                setEventSequenceLinkedIrps(responses[0]);
            }).catch((error) => {
                toast.error(error.message);
            }).finally(() => {
                setIsRunning(false);
            });
        }
    }, [open]);

    const onLinkedIrpChange = (event: SelectChangeEvent): void => {
        event.stopPropagation();
        event.preventDefault();
        let id = parseInt(event.target.value);
        let irp = eventSequenceLinkedIrps.find((item) => item.id === id) || null;
        if (irp !== null) {
            setCurrentLinkedIrp(irp);
        }
    };


    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperComponent={PaperComponent}
            aria-labelledby='draggable-dialog-title'
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle  style={{ cursor: 'move'}} id='draggable-dialog-title'>
                {`Node Occurrence List ${env?.selection[0]?.get('data').uniqueId}`}
            </DialogTitle>
            <DialogContent dividers style={{minHeight: '300px'}}>
                <FormGroup>
                    <Stack direction='row' spacing={2} justifyContent='start'>
                        <InputLabel tabIndex={-1} id={`start-label-${uuidRef.current}`} htmlFor={`start-value-${uuidRef.current}`}>From: </InputLabel>                  
                        <DatePicker
                            id={`start-value-${uuidRef.current}`}
                            showIcon
                            toggleCalendarOnIconClick
                            selectsStart
                            selected={startValue as Date}
                            startDate={startValue as Date}
                            endDate={endValue as Date}
                            onChange={(date: Date | null): void => onChangeStartValue(date)}
                            placeholderText='Select start date'
                            disabled={isRunning}
                        />
                        
                        
                        <InputLabel tabIndex={-1} id={`end-label-${uuidRef.current}`} htmlFor={`end-value-${uuidRef.current}`}>To: </InputLabel>         
                        <DatePicker
                            id={`end-value-${uuidRef.current}`}
                            showIcon
                            toggleCalendarOnIconClick
                            selectsEnd
                            selected={endValue as Date}
                            startDate={startValue as Date}
                            endDate={endValue as Date}  
                            minDate={startValue as Date}
                            onChange={(date: Date | null): void => onChangeEndValue(date)}
                            placeholderText='Select end date'
                            disabled={isRunning}
                        /> 
                    </Stack>
                    <FormControl sx={{mt: 2}} disabled={isRunning}>
                        <InputLabel tabIndex={-1} id={`linked-irps-label-${uuidRef.current}`} htmlFor={`linked-irps-list-${uuidRef.current}`}>Associated IRPs</InputLabel>
                        <Select
                            labelId={`linked-irps-label-${uuidRef.current}`}
                            id={`linked-irps-list-${uuidRef.current}`}
                            value={currentLinkedIrp ? currentLinkedIrp.id.toString() : ''}
                            label="Linked IRPs"
                            onChange={onLinkedIrpChange}
                            size='small'

                            inputProps={{
                                'role': 'textbox',
                                'tabIndex': 0,
                                'id': `linked-irps-list-${uuidRef.current}`,
                            }}
                            MenuProps={
                                {
                                    PaperProps: {
                                        style: {
                                            maxHeight: 230,
                                        }
                                    }
                                }
                            }
                        >
                            {eventSequenceLinkedIrps.map((item) => (
                                <MenuItem key={item.id} value={item.id}>{item.name} ({item.status})</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormHelperText>{isRunning ? 'Loading IRPs...' : eventSequenceLinkedIrps.length > 0 ? 'Select an IRP' : 'No IRPs available'}</FormHelperText>
                    {isRunning && <Box sx={{ width: '100%', mt: 2 }}>
                        <LinearProgress />
                    </Box>}

                </FormGroup>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={isRunning ? <CircularProgress size={20} /> : null}
                    onClick={(): void => {
                        setCurrentCanvasAction({
                            actionType: CanvasActionType.NODE_OCCURRENCE_LIST_VIEW,
                            query: {
                                startDate: forceUTCDate(startValue),
                                endDate: forceUTCDate(endValue),
                                irpId: currentLinkedIrp ? currentLinkedIrp.id : null,
                                irpName: eventSequenceLinkedIrps.find((item) => item.id === currentLinkedIrp?.id)?.name || null,
                            }
                        });
                        onClose();
                    }}
                    disabled={!currentLinkedIrp || !CanvasActions.canAddOrEditResource(env) || isRunning}
                >
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    )
   
}
